import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { AuthClientApi } from "../../../utils/api";
import NavbarMenu from "../../../Components/SharesNavbarMenu";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { toast } from "react-toastify";
import { Container } from "react-bootstrap";
import "./Transaction.css";

// Helper functions for transaction calculations
// These will be used when the transactions API is implemented
function getCompanyTotal(batch, companyName) {
  return batch.items.reduce(
    (sum, item) => sum + (item.amounts[companyName] || 0),
    0
  );
}

function getCompanySharesTotal(batch, companyName) {
  return batch.items.reduce(
    (sum, item) => sum + (item.sharesMap?.[companyName] || 0),
    0
  );
}

function getUsedCompanies(batch, companies) {
  return companies.filter((co) => getCompanyTotal(batch, co.name) > 0);
}

function recalcItemsWithCustomPrice(batch, companies) {
  const newItems = batch.items.map((item) => {
    let totalLeftover = 0;
    const sharesMap = {};
    const leftoverMap = {};

    companies.forEach((co) => {
      const money = item.amounts[co.name] || 0;
      let shares = 0;
      let leftover = 0;

      if (money > 0 && co.customPrice > 0) {
        shares = Math.floor(money / co.customPrice);
        leftover = money % co.customPrice;
      }

      sharesMap[co.name] = shares;
      leftoverMap[co.name] = leftover;
      totalLeftover += leftover;
    });

    return {
      ...item,
      balance: totalLeftover,
      sharesMap,
      leftoverMap,
    };
  });
  return newItems;
}

function getCompanyLeftoverTotal(batch, companyName) {
  return batch.items.reduce(
    (sum, item) => sum + (item.leftoverMap?.[companyName] || 0),
    0
  );
}

function getGrandTotal(batch, usedCompanies) {
  return usedCompanies.reduce(
    (acc, co) => acc + getCompanyTotal(batch, co.name),
    0
  );
}

function formatNumber(value) {
  if (value === undefined || value === null) return "0";
  return new Intl.NumberFormat("en-US").format(value);
}

export default function Transaction() {
  const history = useHistory();

  // Groups State
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalGroups, setTotalGroups] = useState(0);
  const [groupsPerPage] = useState(10);

  // State for each group's data
  const [groupStates, setGroupStates] = useState({});

  // Companies state (will be populated from API response)
  const [companies, setCompanies] = useState([]);

  // Track loading state for company completion (key: "batchId_companyId")
  const [completingCompanies, setCompletingCompanies] = useState({});

  // Refs for scroll detection
  const tableRefs = useRef({});

  // Fetch Groups Metadata
  const fetchGroups = async (page = 1) => {
    setGroupsLoading(true);
    setGroupsError(null);
    try {
      const response = await AuthClientApi.get(
        `/client/blockshares/fetch-batch-groups-transactions?page=${page}&limit=${groupsPerPage}`
      );
      const data = response.data;

      if (data.data && data.data.batches && data.data.pagination) {
        setGroups(data.data.batches);
        setTotalPages(data.data.pagination.totalPages);
        setTotalGroups(data.data.pagination.totalGroups);
        setCurrentPage(data.data.pagination.page);

        // Initialize group states for new groups
        const newGroupStates = {};
        data.data.batches.forEach((group) => {
          if (!groupStates[group.batch_id]) {
            newGroupStates[group.batch_id] = {
              isExpanded: false,
              transactions: [],
              companies: [],
              companyTotals: [],
              page: 1,
              totalPages: 1,
              total: 0,
              loading: false,
              moveToReportLoading: false,
            };
          }
        });

        if (Object.keys(newGroupStates).length > 0) {
          setGroupStates((prev) => ({ ...prev, ...newGroupStates }));
        }
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (err) {
      setGroupsError(err.message);
      toast.error(err.message || "Failed to load groups");
    } finally {
      setGroupsLoading(false);
    }
  };

  // Fetch Transactions for a specific group
  const fetchGroupTransactions = async (batchId, page = 1, append = false) => {
    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        loading: true,
      },
    }));

    try {
      const response = await AuthClientApi.get(
        `/client/blockshares/fetch-transactions?limit=10&page=${page}&batchId=${batchId}`
      );
      const data = response.data;

      if (
        data.status &&
        data.data &&
        data.data.transactions &&
        Array.isArray(data.data.transactions)
      ) {
        // Extract unique companies with their info
        const companiesMap = new Map();

        // Loop through plans and their planCompanies
        data.data.transactions.forEach((plan) => {
          // Check if planCompanies exists and is an array
          if (!plan.planCompanies || !Array.isArray(plan.planCompanies)) {
            return;
          }

          plan.planCompanies.forEach((company) => {
            if (!companiesMap.has(company.companyName)) {
              companiesMap.set(company.companyName, {
                name: company.companyName,
                companyId: company.companyId,
                staticPrice: parseFloat(company.companyClosingPrice),
                customPrice: parseFloat(company.companyClosingPrice), // Initialize with closing price
                status:
                  company.transactionStatus === "Completed"
                    ? "TransactionCompleted"
                    : "InProgress",
              });
            }
          });
        });
        const companiesFromAPI = Array.from(companiesMap.values());

        // Group transactions by planId to create rows
        const transactionsByPlan = new Map();

        data.data.transactions.forEach((plan) => {
          // Check if planCompanies exists and is not empty
          if (
            !plan.planCompanies ||
            !Array.isArray(plan.planCompanies) ||
            plan.planCompanies.length === 0
          ) {
            return;
          }

          const planId = plan.planId;

          // Initialize plan data if not exists
          if (!transactionsByPlan.has(planId)) {
            // Get date from first company's movedToTransactionAt
            const firstCompany = plan.planCompanies[0];
            transactionsByPlan.set(planId, {
              planId: planId,
              customerId: plan.customerId,
              accNumber: plan.trading_number,
              date: new Date(
                firstCompany.movedToTransactionAt
              ).toLocaleDateString("en-GB"),
              amounts: {}, // Fund Price: allocatedAmount
              companyStatus: {},
              transactions: {}, // Contains purchaseAmount, sharesPurchased, finalPrice, cashBalance, closingPrice
            });
          }

          const planData = transactionsByPlan.get(planId);

          // Loop through each company in this plan
          plan.planCompanies.forEach((company) => {
            // Check if transactions array exists and is not empty
            if (
              !company.transactions ||
              !Array.isArray(company.transactions) ||
              company.transactions.length === 0
            ) {
              return;
            }

            const transDetail = company.transactions[0]; // Get first transaction detail

            // Store Fund Price (allocatedAmount)
            planData.amounts[company.companyName] = parseFloat(
              company.allocatedAmount
            );

            // Store company status
            planData.companyStatus[company.companyName] =
              company.transactionStatus === "Completed"
                ? "TransactionCompleted"
                : "InProgress";

            // Store all transaction details from API
            planData.transactions[company.companyName] = {
              transactionId: transDetail.transactionId,
              planCompanyId: company.planCompanyId,
              purchaseAmount: parseFloat(transDetail.purchaseAmount || 0),
              sharesPurchased: transDetail.sharesPurchased || 0,
              finalPrice: parseFloat(transDetail.finalPrice || 0),
              cashBalance: parseFloat(transDetail.cashBalance || 0),
              closingPrice: parseFloat(transDetail.closingPrice || 0),
              completedAt: transDetail.completedAt,
            };
          });
        });

        const transformedTransactions = Array.from(transactionsByPlan.values());

        // Update global companies state if not already set
        if (companies.length === 0) {
          setCompanies(companiesFromAPI);
        } else {
          // Merge with existing companies, preserving customPrice changes
          const mergedCompanies = companiesFromAPI.map((apiCo) => {
            const existing = companies.find((c) => c.name === apiCo.name);
            return existing
              ? { ...apiCo, customPrice: existing.customPrice }
              : apiCo;
          });
          setCompanies(mergedCompanies);
        }

        setGroupStates((prev) => ({
          ...prev,
          [batchId]: {
            ...prev[batchId],
            transactions: append
              ? [...prev[batchId].transactions, ...transformedTransactions]
              : transformedTransactions,
            companies: companiesFromAPI,
            page: data.data.pagination.page,
            totalPages: data.data.pagination.totalPages,
            total: data.data.pagination.totalRecords,
            loading: false,
          },
        }));
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (err) {
      toast.error(err.message || "Failed to load transactions");
      setGroupStates((prev) => ({
        ...prev,
        [batchId]: {
          ...prev[batchId],
          loading: false,
        },
      }));
    }
  };

  useEffect(() => {
    fetchGroups(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchGroups(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchGroups(currentPage + 1);
    }
  };

  const toggleExpand = (batchId) => {
    const isCurrentlyExpanded = groupStates[batchId]?.isExpanded;

    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        isExpanded: !isCurrentlyExpanded,
      },
    }));

    // Fetch transactions on first expand (API in development)
    if (
      !isCurrentlyExpanded &&
      groupStates[batchId]?.transactions.length === 0
    ) {
      fetchGroupTransactions(batchId, 1);
    }
  };

  const handleCustomPriceChange = (batchId, companyName, newPrice) => {
    const price = parseFloat(newPrice) || 0;

    // Update global companies state
    setCompanies((prev) =>
      prev.map((co) =>
        co.name === companyName ? { ...co, customPrice: price } : co
      )
    );

    // Update group-specific companies state
    setGroupStates((prev) => {
      const groupState = prev[batchId];
      if (!groupState) return prev;

      // Update company price only (no recalculation)
      const updatedCompanies = groupState.companies.map((co) =>
        co.name === companyName ? { ...co, customPrice: price } : co
      );

      return {
        ...prev,
        [batchId]: {
          ...groupState,
          companies: updatedCompanies,
        },
      };
    });
  };

  const handleMoveToReport = async (batchId) => {
    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        moveToReportLoading: true,
      },
    }));

    try {
      const response = await AuthClientApi.put(
        `/client/blockshares/move-transaction-to-report?batchId=${batchId}`
      );

      if (response.data.success) {
        toast.success(response.data.message || "Successfully moved to report");

        // Navigate to report page
        history.push("/shares-platform/report");
      } else {
        toast.error(response.data.message || "Failed to move to report");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Failed to move to report"
      );
    } finally {
      setGroupStates((prev) => ({
        ...prev,
        [batchId]: {
          ...prev[batchId],
          moveToReportLoading: false,
        },
      }));
    }
  };

  const handleCompleteCompany = async (batchId, companyId, companyName) => {
    const loadingKey = `${batchId}_${companyId}`;

    // Get the custom price for this company
    const groupState = groupStates[batchId];
    const company = groupState?.companies.find(
      (co) => co.companyId === companyId
    );
    const finalSharePrice = company?.customPrice;

    if (!finalSharePrice || finalSharePrice <= 0) {
      toast.error("Please enter a valid final price");
      return;
    }

    // Set loading state
    setCompletingCompanies((prev) => ({
      ...prev,
      [loadingKey]: true,
    }));

    try {
      const response = await AuthClientApi.post(
        "/client/blockshares/complete-transaction",
        {
          company_id: companyId,
          batch_id: batchId,
          final_share_price: finalSharePrice,
        }
      );

      if (response.data.status) {
        toast.success(
          response.data.message || "Transaction completed successfully"
        );

        // Refetch transactions to get updated data
        await fetchGroupTransactions(batchId, groupState.page);
      } else {
        toast.error(response.data.message || "Failed to complete transaction");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to complete company transaction"
      );
    } finally {
      // Clear loading state
      setCompletingCompanies((prev) => ({
        ...prev,
        [loadingKey]: false,
      }));
    }
  };

  const handleBackToDashboard = () => {
    history.push("/shares-platform");
  };

  return (
    <>
      <NavbarMenu />
      <div className="transaction-wrapper">
        <button
          onClick={handleBackToDashboard}
          className="back-to-dashboard-btn"
          title="Back to Dashboard"
        >
          <ArrowBackIcon />
        </button>
        <h1 className="transaction-title">Transactions</h1>

        <Container fluid className="transaction-container fade-in">
          {/* Loading State */}
          {groupsLoading && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {groupsError && (
            <div className="alert alert-danger" role="alert">
              Error loading groups: {groupsError}
              <button
                className="btn btn-outline-danger btn-sm ms-2"
                onClick={() => fetchGroups(currentPage)}
              >
                Retry
              </button>
            </div>
          )}

          {/* No Groups */}
          {!groupsLoading && !groupsError && groups.length === 0 && (
            <div className="transaction-empty">
              <p>No Transactions</p>
            </div>
          )}

          {/* Render Groups */}
          {!groupsLoading &&
            !groupsError &&
            groups.length > 0 &&
            groups.map((group, index) => {
              const groupState = groupStates[group.batch_id] || {
                isExpanded: false,
                transactions: [],
                companies: [],
                loading: false,
                moveToReportLoading: false,
              };

              const isExpanded = groupState.isExpanded;
              const groupNumber = (currentPage - 1) * groupsPerPage + index + 1;
              const currentTransactions = groupState.transactions;
              const currentCompanies = groupState.companies;
              const currentLoading = groupState.loading;

              return (
                <div key={group.batch_id} className="transaction-batch-card">
                  <div className="transaction-batch-header">
                    <div className="transaction-batch-info">
                      <span className="transaction-batch-title">
                        Group No. {groupNumber}
                      </span>
                      {isExpanded && (
                        <span
                          style={{
                            marginLeft: "15px",
                            color: "#666",
                            fontSize: "14px",
                          }}
                        >
                          Total Transactions: {currentTransactions.length}
                          {/* Total Transactions: {group.total_transactions} */}
                        </span>
                      )}
                    </div>
                    <div className="transaction-batch-buttons">
                      <button
                        className="btn-trans-expand"
                        onClick={() => toggleExpand(group.batch_id)}
                      >
                        {isExpanded ? "Collapse" : "Expand"}
                      </button>
                      {isExpanded && (
                        <button
                          className="btn-trans-complete-batch"
                          onClick={() => handleMoveToReport(group.batch_id)}
                          disabled={
                            groupState.moveToReportLoading ||
                            currentTransactions.length === 0
                          }
                        >
                          {groupState.moveToReportLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Moving...
                            </>
                          ) : (
                            "Move To Report"
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <>
                      {currentLoading ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ padding: "40px" }}
                        >
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">
                              Loading transactions...
                            </span>
                          </div>
                        </div>
                      ) : currentTransactions.length === 0 ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "40px",
                            color: "#666",
                          }}
                        >
                          No transactions available for this group.
                        </div>
                      ) : (
                        <div className="custom-scroll">
                          <table className="table table-bordered transaction-table">
                            <thead>
                              <tr className="transaction-header-row">
                                <th className="sticky-col">Acc #</th>
                                <th className="sticky-col-2">Date</th>
                                {currentCompanies.map((co) => {
                                  // Determine the price to display in input
                                  let displayPrice = co.customPrice || "";

                                  // If transaction is completed, show finalPrice from transactions
                                  if (co.status === "TransactionCompleted") {
                                    // Get finalPrice from first transaction for this company
                                    const firstTrans = currentTransactions.find(
                                      (item) => item.transactions?.[co.name]
                                    );
                                    if (
                                      firstTrans?.transactions?.[co.name]
                                        ?.finalPrice
                                    ) {
                                      displayPrice =
                                        firstTrans.transactions[co.name]
                                          .finalPrice;
                                    }
                                  }

                                  return (
                                    <th key={co.name}>
                                      <div className="company-col">
                                        <span>{co.name}</span>
                                        <div className="price-label">
                                          (CLOSE PRICE: {co.staticPrice})
                                        </div>
                                        <div className="price-label">
                                          FINAL PRICE:{" "}
                                          <input
                                            type="number"
                                            value={displayPrice}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) =>
                                              handleCustomPriceChange(
                                                group.batch_id,
                                                co.name,
                                                e.target.value
                                              )
                                            }
                                            disabled={
                                              co.status ===
                                              "TransactionCompleted"
                                            }
                                          />
                                        </div>
                                        <button
                                          className={`btn-trans-complete-company ${
                                            co.status === "TransactionCompleted"
                                              ? "clicked"
                                              : ""
                                          }`}
                                          disabled={
                                            co.status ===
                                              "TransactionCompleted" ||
                                            completingCompanies[
                                              `${group.batch_id}_${co.companyId}`
                                            ]
                                          }
                                          onClick={() =>
                                            handleCompleteCompany(
                                              group.batch_id,
                                              co.companyId,
                                              co.name
                                            )
                                          }
                                        >
                                          {completingCompanies[
                                            `${group.batch_id}_${co.companyId}`
                                          ] ? (
                                            <>
                                              <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                              ></span>
                                              Completing...
                                            </>
                                          ) : co.status ===
                                            "TransactionCompleted" ? (
                                            "Completed"
                                          ) : (
                                            "Complete"
                                          )}
                                        </button>
                                      </div>
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {currentTransactions.map((item, iItem) => (
                                <tr key={iItem}>
                                  <td className="sticky-col">
                                    {item.accNumber}
                                  </td>
                                  <td className="sticky-col-2">{item.date}</td>
                                  {currentCompanies.map((co) => {
                                    const fundPrice =
                                      item.amounts?.[co.name] || 0;
                                    const trans = item.transactions?.[co.name];
                                    const coStatus =
                                      item.companyStatus?.[co.name] || "N/A";

                                    // Get data from API response
                                    const purchaseAmount =
                                      trans?.purchaseAmount || 0;
                                    const shares = trans?.sharesPurchased || 0;
                                    const finalPrice = trans?.finalPrice || 0;
                                    const cashBalance = trans?.cashBalance || 0;

                                    return (
                                      <td key={co.name}>
                                        <div>
                                          Fund Price: {formatNumber(fundPrice)}
                                        </div>
                                        <div>
                                          Purchase amount:{" "}
                                          {formatNumber(purchaseAmount)}
                                        </div>
                                        <div>
                                          Shares: {formatNumber(shares)}
                                        </div>
                                        {coStatus ===
                                          "TransactionCompleted" && (
                                          <>
                                            <div className="final-price-line">
                                              Final Price:{" "}
                                              {formatNumber(finalPrice)}
                                            </div>
                                            <div className="total-balance-line">
                                              Cash balance:{" "}
                                              {formatNumber(cashBalance)}
                                            </div>
                                          </>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                            {/* <tfoot>
                              <tr className="transaction-total-row">
                                <td className="sticky-col">Total</td>
                                <td className="sticky-col-2"></td>
                                {currentCompanies.map((co) => {
                                  // Calculate totals from transactions
                                  const totalFundPrice =
                                    currentTransactions.reduce(
                                      (sum, item) =>
                                        sum + (item.amounts?.[co.name] || 0),
                                      0
                                    );
                                  const totalPurchaseAmount =
                                    currentTransactions.reduce(
                                      (sum, item) =>
                                        sum +
                                        (item.transactions?.[co.name]
                                          ?.purchaseAmount || 0),
                                      0
                                    );
                                  const totalShares =
                                    currentTransactions.reduce(
                                      (sum, item) =>
                                        sum +
                                        (item.transactions?.[co.name]
                                          ?.sharesPurchased || 0),
                                      0
                                    );
                                  const totalCashBalance =
                                    currentTransactions.reduce(
                                      (sum, item) =>
                                        sum +
                                        (item.transactions?.[co.name]
                                          ?.cashBalance || 0),
                                      0
                                    );
                                  const hasCompleted =
                                    co.status === "TransactionCompleted";

                                  return (
                                    <td key={co.name}>
                                      <div>
                                        Fund Price:{" "}
                                        {formatNumber(totalFundPrice)}
                                      </div>
                                      <div>
                                        Purchase Amount:{" "}
                                        {formatNumber(totalPurchaseAmount)}
                                      </div>
                                      <div>
                                        Shares: {formatNumber(totalShares)}
                                      </div>
                                      {hasCompleted && (
                                        <div>
                                          Total balance:{" "}
                                          {formatNumber(totalCashBalance)}
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tfoot> */}
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
        </Container>

        {/* Pagination - Previous/Next Buttons */}
        {!groupsLoading && !groupsError && groups.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 40px",
              marginTop: "20px",
            }}
          >
            {/* Left - Previous Button */}
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              style={{
                padding: "10px 20px",
                backgroundColor: currentPage === 1 ? "#3f3838" : "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "600",
                visibility: currentPage === 1 ? "hidden" : "visible",
              }}
            >
              ← Previous Page
            </button>

            {/* Center - Page Info */}
            <span
              style={{
                color: "#666",
                fontSize: "14px",
              }}
            >
              Page {currentPage} of {totalPages} ({totalGroups} groups total)
            </span>

            {/* Right - Next Button */}
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{
                padding: "10px 20px",
                backgroundColor:
                  currentPage === totalPages ? "#3f3838" : "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "600",
                visibility: currentPage === totalPages ? "hidden" : "visible",
              }}
            >
              Next Page →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
